import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { addMonths, format, getUnixTime, isSameDay, startOfDay, subMonths } from 'date-fns';
import { dropTask } from 'ember-concurrency';
import { or } from 'ember-truth-helpers';
import CalendarGrid from 'garaje/components/calendar-grid';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type ActiveDayConfig from 'garaje/types/date-time/active-day-config';
import type DateLimits from 'garaje/types/date-time/date-limits';
import type DayConfig from 'garaje/types/date-time/day-config';

interface DayData {
  date: Date;
  screeningCard?: { id: string } | null;
  reservations?: unknown[];
  reservation?: unknown;
}

interface LendingStatus {
  date: string;
  lendingStatus: string;
  reservation?: {
    reservationId: number;
    bookedByEmployeeId: number;
    bookedByName: string;
    bookedByEmail: string;
    startTime: string;
    endTime: string;
  } | null;
}

interface MultiDateScheduleModalSignature {
  Args: {
    onClose: () => void;
    onSchedule: (dates: string[]) => Promise<void>;
    onLend: (dates: string[]) => Promise<void>;
    lendingStatusByDate: Record<string, LendingStatus>;
    deskLendingEnabled: boolean;
    bookDeskEnabled: boolean;
    days: DayData[];
    activeDays?: ActiveDayConfig[];
  };
}

export default class MultiDateScheduleModal extends Component<MultiDateScheduleModalSignature> {
  @tracked selectedDates: Date[] = [];
  @tracked currentMonth = new Date();
  @tracked calendarOpen = false;

  get dateLimits(): DateLimits {
    return { startDate: getUnixTime(startOfDay(new Date())) };
  }

  get noSelection(): boolean {
    return this.selectedDates.length === 0;
  }

  get currentMonthLabel(): string {
    return format(this.currentMonth, 'MMM yyyy');
  }

  get formattedSelectedDates(): Array<{ date: Date; label: string }> {
    return [...this.selectedDates]
      .sort((a, b) => a.getTime() - b.getTime())
      .map((date) => ({
        date,
        label: format(date, 'MMM d, yyyy'),
      }));
  }

  get daysToCustomize(): DayConfig[] {
    const configs: DayConfig[] = [];

    const { days, lendingStatusByDate } = this.args;

    for (const day of days) {
      const date = day.date instanceof Date ? day.date : new Date(day.date);
      const dateKey = format(date, 'yyyy-MM-dd');
      const unixTimestamp = getUnixTime(startOfDay(date));

      const hasInvite = !!day.screeningCard?.id;
      const hasReservations = (day.reservations && day.reservations.length > 0) || !!day.reservation;
      const lendingEntry = lendingStatusByDate[dateKey];
      const isLentAndBooked = lendingEntry?.lendingStatus === 'LENT_BOOKED';

      if (hasInvite || hasReservations || isLentAndBooked) {
        let tooltip = '';
        if (isLentAndBooked) {
          tooltip = 'Your desk is lent and booked by someone else for this day';
        } else if (hasReservations) {
          tooltip = 'You already have a reservation for this day';
        } else if (hasInvite) {
          tooltip = 'You are already scheduled for this day';
        }

        configs.push({
          date: unixTimestamp,
          disabled: true,
          class: 'opacity-40 multi-date-disabled',
          tooltip,
        });
      }
    }

    return configs;
  }

  lendTask = dropTask(async () => {
    const dates = this.selectedDates.map((d) => format(d, 'yyyy-MM-dd'));
    await this.args.onLend(dates);
    this.args.onClose();
  });

  scheduleTask = dropTask(async () => {
    const dates = this.selectedDates.map((d) => format(d, 'yyyy-MM-dd'));
    await this.args.onSchedule(dates);
    this.args.onClose();
  });

  @action
  toggleCalendar(): void {
    this.calendarOpen = !this.calendarOpen;
  }

  @action
  toggleDate(date: Date): void {
    if (date < startOfDay(new Date())) return;

    const { activeDays } = this.args;
    if (activeDays && activeDays.length > 0) {
      const dayOfWeek = format(date, 'EEEE');
      if (!activeDays.some((d) => d.dayOfWeek === dayOfWeek)) return;
    }

    const existing = this.selectedDates.find((d) => isSameDay(d, date));
    if (existing) {
      this.selectedDates = this.selectedDates.filter((d) => !isSameDay(d, date));
    } else {
      this.selectedDates = [...this.selectedDates, date];
    }
  }

  @action
  removeDate(date: Date, e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();
    this.selectedDates = this.selectedDates.filter((d) => !isSameDay(d, date));
  }

  @action
  previousMonth(): void {
    this.currentMonth = subMonths(this.currentMonth, 1);
  }

  @action
  nextMonth(): void {
    this.currentMonth = addMonths(this.currentMonth, 1);
  }

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @onClose={{@onClose}}
      @translucentOverlay={{true}}
      @targetAttachment="center"
    >
      <div class="p-6" style="min-width: 400px;" data-test-multi-date-schedule-modal>
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold">Select multiple dates</h2>
          <button
            type="button"
            class="btn btn--small btn--outline btn--cancel"
            aria-label="close"
            {{on "click" @onClose}}
            data-test-multi-date-close
          >
            &times;
          </button>
        </div>

        {{! Date select combobox }}
        <div class="mb-4">
          <div
            class="w-full flex items-center border border-carbon-20 rounded-md px-3 py-2 bg-white text-left cursor-pointer focus:ring-2 focus:ring-gem-50"
            {{on "click" this.toggleCalendar}}
            data-test-date-select-trigger
          >
            <div class="flex-1 flex flex-wrap gap-1 items-center min-h-[24px]">
              {{#if this.formattedSelectedDates.length}}
                {{#each this.formattedSelectedDates as |entry|}}
                  <span class="inline-flex items-center bg-carbon-5 rounded px-2 py-0.5 text-sm" data-test-date-chip>
                    <button
                      type="button"
                      class="mr-1 text-base leading-none text-carbon-50 hover:text-carbon-90 cursor-pointer"
                      aria-label="Remove {{entry.label}}"
                      {{on "click" (fn this.removeDate entry.date)}}
                      data-test-remove-date
                    >
                      &times;
                    </button>
                    {{entry.label}}
                  </span>
                {{/each}}
              {{else}}
                <span class="text-carbon-40">Select dates</span>
              {{/if}}
            </div>
            <img
              src="/assets/images/chevron-down.svg"
              alt=""
              class="ml-2 w-3 h-3 transition-transform {{if this.calendarOpen 'rotate-180'}}"
            />
          </div>

          {{#if this.calendarOpen}}
            <div
              class="mt-2 p-3 border border-carbon-20 rounded-md multi-date-calendar-grid"
              data-test-calendar-dropdown
            >
              <div class="flex items-center justify-between mb-2">
                <button
                  type="button"
                  class="btn btn--small btn--outline !border-none"
                  {{on "click" this.previousMonth}}
                  data-test-prev-month
                >
                  &larr;
                </button>
                <span class="font-medium text-sm">
                  {{this.currentMonthLabel}}
                </span>
                <button
                  type="button"
                  class="btn btn--small btn--outline !border-none"
                  {{on "click" this.nextMonth}}
                  data-test-next-month
                >
                  &rarr;
                </button>
              </div>

              <CalendarGrid
                @selectedDates={{this.selectedDates}}
                @onSelectDate={{this.toggleDate}}
                @daysToCustomize={{this.daysToCustomize}}
                @dateLimits={{this.dateLimits}}
                @currentMonth={{this.currentMonth}}
                @activeDays={{@activeDays}}
                @respectSchedulingLimits={{true}}
              />
            </div>
          {{/if}}
        </div>

        <div class="flex justify-end gap-3">
          {{#if @deskLendingEnabled}}
            <button
              type="button"
              class="btn btn--small btn--outline"
              disabled={{or this.noSelection this.lendTask.isRunning this.scheduleTask.isRunning}}
              {{on "click" this.lendTask.perform}}
              data-test-multi-date-lend
            >
              {{if this.lendTask.isRunning "Lending desk..." "Lend desk"}}
            </button>
          {{/if}}
          {{#if @bookDeskEnabled}}
            <button
              type="button"
              class="btn btn--small btn--brand"
              disabled={{or this.noSelection this.lendTask.isRunning this.scheduleTask.isRunning}}
              {{on "click" this.scheduleTask.perform}}
              data-test-multi-date-schedule
            >
              {{if this.scheduleTask.isRunning "Scheduling..." "Schedule"}}
            </button>
          {{/if}}
        </div>
      </div>
    </ModalDialog>
  </template>
}
